import { faGlobe, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppLayout, Button, Stack, Text } from '@sixui/ui';
import { navigationDrawerContentTokens } from '@sixui/ui/components/NavigationDrawerContent/NavigationDrawerContent.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { Logo } from '~/components/atoms/Logo';
import { useFeedbackDialog } from '~/hooks/useFeedbackDialog';
import { useApi } from '~/lib/api';
import { appRegionsSettings } from '~/lib/appSettings';
import { AppSidebarContent } from './AppSidebarContent';
import { OrganizationSidebarContent } from './OrganizationSidebarContent';
import { PersonalSidebarContent } from './PersonalSidebarContent';
import { SidebarHeader } from './SidebarHeader';
import { SidebarUserItem } from './SidebarUserItem';

export type ISidebarProps = IContainerProps & {
  appId?: string;
  organizationId?: string;
};

const styles = stylex.create({
  host: {
    [navigationDrawerContentTokens.containerColor]:
      colorSchemeTokens.surfaceContainerLow,
  },
  region: {
    paddingLeft: '0.5rem',
  },
  logo: {
    height: 48,
  },
  reportIssue: {
    padding: '0.5rem',
    display: 'flex',
    justifyContent: 'center',
  },
});

export const Sidebar: React.FC<ISidebarProps> = (props) => {
  const { sx, organizationId, appId, ...other } = props;
  const { t } = useTranslation();
  const feedbackDialog = useFeedbackDialog();
  const api = useApi();
  const appRegion = api.getAuthorization()?.region;
  const appRegionName = appRegion
    ? appRegionsSettings[appRegion].name
    : undefined;

  const homeUrl = organizationId ? `/organizations/${organizationId}` : '/apps';

  return (
    <AppLayout.NavigationDrawer
      sx={[styles.host, sx]}
      divider
      headline={
        <Stack horizontal justify="space-between">
          <Logo sx={styles.logo} href={homeUrl} />
          {appRegionName ? (
            <Stack as={Text} horizontal gap={2} variant="label" size="sm">
              <FontAwesomeIcon icon={faGlobe} />
              {appRegionName}
            </Stack>
          ) : null}
        </Stack>
      }
      closeIcon={<FontAwesomeIcon icon={faXmark} />}
      header={<SidebarHeader organizationId={organizationId} appId={appId} />}
      footer={
        <>
          {feedbackDialog.enabled ? (
            <div {...stylex.props(styles.reportIssue)}>
              <Button onClick={feedbackDialog.open} variant="text">
                {t('SidebarHeader.report-issue')}
              </Button>
            </div>
          ) : null}

          <SidebarUserItem />
        </>
      }
      showCloseButton
      {...other}
    >
      {appId ? (
        <AppSidebarContent appId={appId} />
      ) : organizationId ? (
        <OrganizationSidebarContent organizationId={organizationId} />
      ) : (
        <PersonalSidebarContent />
      )}
    </AppLayout.NavigationDrawer>
  );
};
